import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { ChangeEvent, FocusEvent, InputHTMLAttributes, KeyboardEvent, ReactNode, Ref } from 'react';
import TextField from '../TextField';
import styles from './Combobox.module.scss';
import { getListboxPlacement, getVisibleViewport } from './placement';

/** The status region's copy: what a screen reader hears as the list changes. */
export interface ComboboxMessages {
  /** While `loading` is true. */
  loading: string;
  /** While the list is open: the count of options and how to use them. */
  results: (count: number) => string;
  /** Otherwise (closed, or nothing to suggest). */
  idle: string;
}

/** What `renderOption` receives beside the option. */
export interface ComboboxOptionState {
  /** The option the arrow keys (or the pointer) are on. */
  active: boolean;
  index: number;
}
type ComboboxInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'style' | 'type' | 'size' | 'children' | 'list' | 'role' | 'onSelect' | 'aria-autocomplete' | 'aria-expanded' | 'aria-controls' | 'aria-activedescendant' | 'aria-busy'>;
export type ComboboxProps<Option> = ComboboxInputProps & {
  /** The input's id: FormGroup's `htmlFor` names it. */
  id: string;
  /** The listbox's accessible name ("Address suggestions"). */
  listLabel: string;
  /** The options to suggest; the list shows only while there are some. */
  options: readonly Option[];
  /** An option's text (its label and the change key). Defaults to `String`. */
  getOptionLabel?: (option: Option) => string;
  /** A stable React key; defaults to the label and the index. */
  getOptionKey?: (option: Option, index: number) => string;
  /** The option's content; defaults to its label. */
  renderOption?: (option: Option, state: ComboboxOptionState) => ReactNode;
  /** Enter on the active option, or a click on one. The list closes. */
  onSelect: (option: Option, index: number) => void;
  /** Controlled open state; typing and the arrow keys request `true`. */
  open?: boolean;
  /** Uncontrolled initial open state. Default `false`. */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Controlled active option (`-1`: none). */
  activeIndex?: number;
  /** Uncontrolled initial active option. Default `-1`. */
  defaultActiveIndex?: number;
  onActiveIndexChange?: (index: number) => void;
  /** Options are being fetched: `aria-busy` and the loading status. */
  loading?: boolean;
  /** The status region's copy; each key falls back to the generic copy. */
  messages?: Partial<ComboboxMessages>;
  /** The input element. */
  ref?: Ref<HTMLInputElement>;
  className?: never;
  style?: never;
};
export const DEFAULT_COMBOBOX_MESSAGES: ComboboxMessages = {
  loading: 'Loading suggestions.',
  results: count => `${count} ${count === 1 ? 'suggestion' : 'suggestions'} available. Use the up and down arrow keys to review them, then press Enter to select one.`,
  idle: ''
};

/**
 * A value that is either controlled (`value` defined) or kept here, with a
 * change callback that fires only on a change.
 */
function useControllable(value, initial, onChange) {
  const $ = _c(7);
  const [own, setOwn] = useState(initial);
  const controlled = value !== undefined;
  const current = controlled ? value : own;
  let t0;
  if ($[0] !== controlled || $[1] !== current || $[2] !== onChange) {
    t0 = next => {
      if (Object.is(next, current)) {
        return;
      }
      if (!controlled) {
        setOwn(next);
      }
      onChange?.(next);
    };
    $[0] = controlled;
    $[1] = current;
    $[2] = onChange;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const set = t0;
  let t1;
  if ($[4] !== current || $[5] !== set) {
    t1 = [current, set];
    $[4] = current;
    $[5] = set;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1 as const;
}
function assignRef<T>(ref: Ref<T> | undefined, node: T | null) {
  if (typeof ref === 'function') ref(node);else if (ref) (ref as {
    current: T | null;
  }).current = node;
}

/**
 * A text input that suggests options from a list: the APG combobox with
 * list autocomplete and manual selection. The input is a Helios TextField
 * (`role="combobox"`); the options are a listbox the input owns through
 * `aria-controls` and `aria-activedescendant`, so focus never leaves the
 * input and the options are not in the Tab order. Up and Down move the
 * active option (and open the list), Enter selects it, Escape closes the
 * list and keeps focus, Tab leaves the field and closes it. A polite status
 * region (`<listbox id>-status`, in `aria-describedby`) announces loading
 * and the count. The consumer fetches and filters the options; the
 * component never changes the input's value.
 */
function Combobox(t0) {
  const $ = _c(100);
  let activeIndexProp;
  let describedBy;
  let getOptionKey;
  let id;
  let inputProps;
  let listLabel;
  let messages;
  let onActiveIndexChange;
  let onBlur;
  let onChange;
  let onKeyDown;
  let onOpenChange;
  let onSelect;
  let openProp;
  let options;
  let ref;
  let renderOption;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    const {
      id: t5,
      listLabel: t6,
      options: t7,
      getOptionLabel: t8,
      getOptionKey: t9,
      renderOption: t10,
      onSelect: t11,
      open: t12,
      defaultOpen: t13,
      onOpenChange: t14,
      activeIndex: t15,
      defaultActiveIndex: t16,
      onActiveIndexChange: t17,
      loading: t18,
      messages: t19,
      ref: t20,
      className: _className,
      style: _style,
      "aria-describedby": t21,
      onChange: t22,
      onKeyDown: t23,
      onBlur: t24,
      ...t25
    } = t0;
    id = t5;
    listLabel = t6;
    options = t7;
    t1 = t8;
    getOptionKey = t9;
    renderOption = t10;
    onSelect = t11;
    openProp = t12;
    t2 = t13;
    onOpenChange = t14;
    activeIndexProp = t15;
    t3 = t16;
    onActiveIndexChange = t17;
    t4 = t18;
    messages = t19;
    ref = t20;
    describedBy = t21;
    onChange = t22;
    onKeyDown = t23;
    onBlur = t24;
    inputProps = t25;
    $[0] = t0;
    $[1] = activeIndexProp;
    $[2] = describedBy;
    $[3] = getOptionKey;
    $[4] = id;
    $[5] = inputProps;
    $[6] = listLabel;
    $[7] = messages;
    $[8] = onActiveIndexChange;
    $[9] = onBlur;
    $[10] = onChange;
    $[11] = onKeyDown;
    $[12] = onOpenChange;
    $[13] = onSelect;
    $[14] = openProp;
    $[15] = options;
    $[16] = ref;
    $[17] = renderOption;
    $[18] = t1;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
  } else {
    activeIndexProp = $[1];
    describedBy = $[2];
    getOptionKey = $[3];
    id = $[4];
    inputProps = $[5];
    listLabel = $[6];
    messages = $[7];
    onActiveIndexChange = $[8];
    onBlur = $[9];
    onChange = $[10];
    onKeyDown = $[11];
    onOpenChange = $[12];
    onSelect = $[13];
    openProp = $[14];
    options = $[15];
    ref = $[16];
    renderOption = $[17];
    t1 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
  }
  const getOptionLabel = t1 === undefined ? String : t1;
  const defaultOpen = t2 === undefined ? false : t2;
  const defaultActiveIndex = t3 === undefined ? -1 : t3;
  const loading = t4 === undefined ? false : t4;
  const listboxId = useId();
  const statusId = `${listboxId}-status`;
  const inputRef = useRef(null);
  const listboxRef = useRef(null);
  let t5;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [];
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const optionRefs = useRef(t5);
  const [open, setOpen] = useControllable(openProp, defaultOpen, onOpenChange);
  const [activeIndex, setActiveIndex] = useControllable(activeIndexProp, defaultActiveIndex, onActiveIndexChange);
  const copy = {
    ...DEFAULT_COMBOBOX_MESSAGES,
    ...messages
  };
  const expanded = open && options.length > 0;
  const active = expanded && activeIndex >= 0 && activeIndex < options.length ? activeIndex : -1;
  let t6;
  if ($[23] !== listboxId) {
    t6 = index => `${listboxId}-option-${index}`;
    $[23] = listboxId;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const optionId = t6;
  let t7;
  if ($[25] !== ref) {
    t7 = node => {
      inputRef.current = node;
      assignRef(ref, node);
    };
    $[25] = ref;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const setInputRef = t7;
  let t8;
  if ($[27] !== getOptionLabel || $[28] !== options) {
    let t9;
    if ($[30] !== getOptionLabel) {
      t9 = option => getOptionLabel(option);
      $[30] = getOptionLabel;
      $[31] = t9;
    } else {
      t9 = $[31];
    }
    t8 = options.map(t9).join("\n");
    $[27] = getOptionLabel;
    $[28] = options;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const optionsKey = t8;
  const previousOptionsKey = useRef(optionsKey);
  let t9;
  if ($[32] !== optionsKey || $[33] !== setActiveIndex) {
    t9 = () => {
      if (previousOptionsKey.current === optionsKey) {
        return;
      }
      previousOptionsKey.current = optionsKey;
      setActiveIndex(-1);
    };
    $[32] = optionsKey;
    $[33] = setActiveIndex;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  useEffect(t9);
  let t10;
  if ($[35] !== expanded) {
    t10 = () => {
      const input = inputRef.current;
      const list = listboxRef.current;
      if (!expanded || !input || !list) {
        return;
      }
      const view = input.ownerDocument.defaultView ?? window;
      const place = () => {
        const field = input.getBoundingClientRect();
        const viewport = getVisibleViewport(view);
        const borders = list.offsetHeight - list.clientHeight;
        const {
          side,
          maxHeight
        } = getListboxPlacement({
          fieldTop: field.top,
          fieldBottom: field.bottom,
          viewportTop: viewport.top,
          viewportBottom: viewport.bottom,
          listHeight: list.scrollHeight + borders
        });
        list.dataset.placement = side;
        if (maxHeight === null) {
          list.style.removeProperty("--helios-combobox-max-block-size");
        } else {
          list.style.setProperty("--helios-combobox-max-block-size", `${maxHeight}px`);
        }
      };
      place();
      const visual = view.visualViewport;
      view.addEventListener("resize", place);
      view.addEventListener("scroll", place, {
        capture: true,
        passive: true
      });
      visual?.addEventListener("resize", place);
      visual?.addEventListener("scroll", place);
      return () => {
        view.removeEventListener("resize", place);
        view.removeEventListener("scroll", place, {
          capture: true
        });
        visual?.removeEventListener("resize", place);
        visual?.removeEventListener("scroll", place);
      };
    };
    $[35] = expanded;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== expanded || $[38] !== optionsKey) {
    t11 = [expanded, optionsKey];
    $[37] = expanded;
    $[38] = optionsKey;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  useLayoutEffect(t10, t11);
  let t12;
  let t13;
  if ($[40] !== active) {
    t12 = () => {
      const list_0 = listboxRef.current;
      const option_0 = active >= 0 ? optionRefs.current[active] : null;
      if (!list_0 || !option_0) {
        return;
      }
      if (option_0.offsetTop < list_0.scrollTop) {
        list_0.scrollTop = option_0.offsetTop;
      } else {
        if (option_0.offsetTop + option_0.offsetHeight > list_0.scrollTop + list_0.clientHeight) {
          list_0.scrollTop = option_0.offsetTop + option_0.offsetHeight - list_0.clientHeight;
        }
      }
    };
    t13 = [active];
    $[40] = active;
    $[41] = t12;
    $[42] = t13;
  } else {
    t12 = $[41];
    t13 = $[42];
  }
  useEffect(t12, t13);
  let t14;
  if ($[43] !== setActiveIndex || $[44] !== setOpen) {
    t14 = () => {
      setOpen(false);
      setActiveIndex(-1);
    };
    $[43] = setActiveIndex;
    $[44] = setOpen;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  const close = t14;
  let t15;
  if ($[46] !== close || $[47] !== onSelect || $[48] !== options) {
    t15 = index_0 => {
      const option_1 = options[index_0];
      if (option_1 === undefined) {
        return;
      }
      close();
      onSelect(option_1, index_0);
    };
    $[46] = close;
    $[47] = onSelect;
    $[48] = options;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  const select = t15;
  let t16;
  if ($[50] !== onChange || $[51] !== setActiveIndex || $[52] !== setOpen) {
    t16 = event => {
      setActiveIndex(-1);
      setOpen(true);
      onChange?.(event);
    };
    $[50] = onChange;
    $[51] = setActiveIndex;
    $[52] = setOpen;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  const handleChange = t16;
  let t17;
  if ($[54] !== active || $[55] !== close || $[56] !== expanded || $[57] !== onKeyDown || $[58] !== options.length || $[59] !== select || $[60] !== setActiveIndex || $[61] !== setOpen) {
    t17 = event_0 => {
      onKeyDown?.(event_0);
      if (event_0.defaultPrevented || event_0.nativeEvent.isComposing) {
        return;
      }
      const last = options.length - 1;
      switch (event_0.key) {
        case "ArrowDown":
        case "ArrowUp":
          {
            if (options.length === 0) {
              return;
            }
            event_0.preventDefault();
            const down = event_0.key === "ArrowDown";
            if (!expanded) {
              setOpen(true);
              if (!event_0.altKey) {
                setActiveIndex(down ? 0 : last);
              }
              return;
            }
            if (down) {
              setActiveIndex(active >= last ? 0 : active + 1);
            } else {
              setActiveIndex(active <= 0 ? last : active - 1);
            }
            return;
          }
        case "Enter":
          {
            if (expanded && active >= 0) {
              event_0.preventDefault();
              select(active);
            }
            return;
          }
        case "Escape":
          {
            if (expanded) {
              event_0.preventDefault();
              event_0.stopPropagation();
              close();
            }
            return;
          }
        case "Tab":
          {
            if (expanded) {
              close();
            }
            return;
          }
        default:
      }
    };
    $[54] = active;
    $[55] = close;
    $[56] = expanded;
    $[57] = onKeyDown;
    $[58] = options.length;
    $[59] = select;
    $[60] = setActiveIndex;
    $[61] = setOpen;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  const handleKeyDown = t17;
  let t18;
  if ($[63] !== close || $[64] !== onBlur) {
    t18 = event_1 => {
      close();
      onBlur?.(event_1);
    };
    $[63] = close;
    $[64] = onBlur;
    $[65] = t18;
  } else {
    t18 = $[65];
  }
  const handleBlur = t18;
  const status = loading ? copy.loading : expanded ? copy.results(options.length) : copy.idle;
  const t19 = active >= 0 ? optionId(active) : undefined;
  let t20;
  if ($[66] !== describedBy || $[67] !== statusId) {
    t20 = [statusId, describedBy].filter(Boolean);
    $[66] = describedBy;
    $[67] = statusId;
    $[68] = t20;
  } else {
    t20 = $[68];
  }
  const t21 = t20.join(" ");
  let t22;
  if ($[69] !== expanded || $[70] !== handleBlur || $[71] !== handleChange || $[72] !== handleKeyDown || $[73] !== id || $[74] !== inputProps || $[75] !== listboxId || $[76] !== loading || $[77] !== setInputRef || $[78] !== t19 || $[79] !== t21) {
    t22 = <TextField {...inputProps} ref={setInputRef} id={id} type="text" role="combobox" aria-autocomplete="list" aria-expanded={expanded} aria-controls={listboxId} aria-activedescendant={t19} aria-busy={loading} aria-describedby={t21} onChange={handleChange} onKeyDown={handleKeyDown} onBlur={handleBlur} />;
    $[69] = expanded;
    $[70] = handleBlur;
    $[71] = handleChange;
    $[72] = handleKeyDown;
    $[73] = id;
    $[74] = inputProps;
    $[75] = listboxId;
    $[76] = loading;
    $[77] = setInputRef;
    $[78] = t19;
    $[79] = t21;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] !== active || $[82] !== expanded || $[83] !== getOptionKey || $[84] !== getOptionLabel || $[85] !== listLabel || $[86] !== listboxId || $[87] !== optionId || $[88] !== options || $[89] !== renderOption || $[90] !== select || $[91] !== setActiveIndex) {
    t23 = expanded && <ul ref={listboxRef} id={listboxId} role="listbox" aria-label={listLabel} className={styles["helios-combobox__listbox"]} onMouseDown={_temp}>{options.map((option_2, index_1) => {
        const label = getOptionLabel(option_2);
        const isActive = index_1 === active;
        return <li key={getOptionKey?.(option_2, index_1) ?? `${label}-${index_1}`} ref={node_0 => {
          optionRefs.current[index_1] = node_0;
        }} id={optionId(index_1)} role="option" aria-selected={isActive} className={styles["helios-combobox__option"]} onMouseMove={() => {
          if (!isActive) {
            setActiveIndex(index_1);
          }
        }} onClick={() => select(index_1)}>{renderOption ? renderOption(option_2, {
            active: isActive,
            index: index_1
          }) : label}</li>;
      })}</ul>;
    $[81] = active;
    $[82] = expanded;
    $[83] = getOptionKey;
    $[84] = getOptionLabel;
    $[85] = listLabel;
    $[86] = listboxId;
    $[87] = optionId;
    $[88] = options;
    $[89] = renderOption;
    $[90] = select;
    $[91] = setActiveIndex;
    $[92] = t23;
  } else {
    t23 = $[92];
  }
  let t24;
  if ($[93] !== status || $[94] !== statusId) {
    t24 = <span id={statusId} className={styles["helios-combobox__status"]} aria-live="polite">{status}</span>;
    $[93] = status;
    $[94] = statusId;
    $[95] = t24;
  } else {
    t24 = $[95];
  }
  let t25;
  if ($[96] !== t22 || $[97] !== t23 || $[98] !== t24) {
    t25 = <div className={styles["helios-combobox"]} data-helios-combobox="">{t22}{t23}{t24}</div>;
    $[96] = t22;
    $[97] = t23;
    $[98] = t24;
    $[99] = t25;
  } else {
    t25 = $[99];
  }
  return t25;
}
function _temp(event_2) {
  return event_2.preventDefault();
}
export default Combobox;